/**
 * Capa de Negocio (BL) para la dinámica "Xolo Fest 2026 — Pasaporte Digital, Zonas y Tómbola Perrona" (Tile 53).
 * Gestiona sesiones en localStorage, validaciones de formulario móvil sin OTP, conexión SignalR en tiempo real
 * y renderizado gráfico en Canvas 2D para descarga PNG de Cupón Dorado, Insignia Leyenda y Pase de Tómbola.
 */

import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import * as signalR from '@microsoft/signalr';
import {
    GetXfPublicConfig,
    ValidateXfTicketToken,
    RegisterXfParticipant,
    RecoverXfPass,
    GetXfDemoSampleTickets,
    GetXfPassportByFolio,
    UpdateXfAdminConfig,
    SimulateXfStamp,
    SimulateXfStock,
    ResetXfParticipantDemo,
    AuthenticateXfDemoOperator,
    LoginXfRealOperator,
    GetXfScannerStations,
    GetXfScannerZones,
    CheckXfAccessScan,
    GetXfRecentAccessLogs,
    GetXfStationProducts,
    LookupXfParticipantForScanner,
    RecordXfPosPurchase,
    RecordXfSponsorActivation,
    RedeemXfRewardCoupon,
    GetXfLiveBoardMetrics,
    SearchXfParticipants,
    GetXfParticipant360,
    SimulateXfTraffic,
    ResetXfDemoTraffic,
    ResetXfAllData,
    ExportXfExcelReport,
    GetOfflineScanQueue,
    EnqueueOfflineScan,
    ClearOfflineScanQueue,
    ProcessOfflineScanQueue
} from '../DL/XoloFest';

export const XF_SESSION_STORAGE_KEY = 'xf_session_id';
export const XF_PARTICIPANT_STORAGE_KEY = 'xf_participant_session';
export const XF_OPERATOR_STORAGE_KEY = 'xf_operator_session';

/**
 * Obtiene o genera un identificador único de sesión para el dispositivo/navegador actual.
 * Se persiste en localStorage y sessionStorage para trazabilidad de rate-limit (X-Xf-Session-Id).
 * @returns {string} Identificador de sesión
 */
export const GetOrCreateXfSessionId = () => {
    try {
        let sessionId =
            localStorage.getItem(XF_SESSION_STORAGE_KEY) ||
            sessionStorage.getItem(XF_SESSION_STORAGE_KEY);

        if (!sessionId) {
            if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
                sessionId = `xf-${crypto.randomUUID()}`;
            } else {
                sessionId = `xf-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
            }
            localStorage.setItem(XF_SESSION_STORAGE_KEY, sessionId);
            sessionStorage.setItem(XF_SESSION_STORAGE_KEY, sessionId);
        }
        return sessionId;
    } catch (error) {
        return `xf-temp-${Date.now()}`;
    }
};

/**
 * Normaliza y guarda en localStorage la sesión activa del participante de Xolo Fest.
 * @param {object} sessionData - Datos retornados por /register, /recover o /passport/{folio}
 * @returns {object|null} Objeto de sesión normalizado y persistido
 */
export const SaveXfParticipantSession = (sessionData) => {
    if (!sessionData) return null;

    try {
        const rawParticipant = sessionData.participant || sessionData.data?.participant || sessionData;
        const allowedZonesRaw =
            rawParticipant.allowedZones ||
            sessionData.allowedZones ||
            ['GEN', 'FAN_ZONE', 'GASTRO'];

        let parsedZones = allowedZonesRaw;
        if (typeof allowedZonesRaw === 'string') {
            try {
                parsedZones = JSON.parse(allowedZonesRaw);
            } catch {
                parsedZones = allowedZonesRaw.split(',').map((z) => z.trim()).filter(Boolean);
            }
        }

        const stampsList = Array.isArray(sessionData.stamps)
            ? sessionData.stamps
            : Array.isArray(rawParticipant.stamps)
            ? rawParticipant.stamps
            : [];

        const stationsList = Array.isArray(sessionData.availableStations)
            ? sessionData.availableStations
            : Array.isArray(rawParticipant.availableStations)
            ? rawParticipant.availableStations
            : [];

        const participant = {
            id: rawParticipant.id ?? rawParticipant.participantId ?? null,
            participantId: rawParticipant.participantId ?? rawParticipant.id ?? null,
            ticketId: rawParticipant.ticketId ?? null,
            ticketFolio: (rawParticipant.ticketFolio || sessionData.ticketFolio || '').toUpperCase(),
            fullName: rawParticipant.fullName || sessionData.fullName || '',
            email: rawParticipant.email || sessionData.email || '',
            phone: rawParticipant.phone || sessionData.phone || '',
            ticketCategory: (
                rawParticipant.ticketCategory ||
                rawParticipant.category ||
                sessionData.ticketCategory ||
                sessionData.category ||
                'GENERAL'
            ).toUpperCase(),
            raffleTicketCode:
                rawParticipant.raffleTicketCode ||
                sessionData.raffleTicketCode ||
                '',
            hasAttended: Boolean(rawParticipant.hasAttended ?? sessionData.hasAttended ?? false),
            firstAccessAtUtc: rawParticipant.firstAccessAtUtc || sessionData.firstAccessAtUtc || null,
            verificationToken:
                rawParticipant.verificationToken ||
                sessionData.verificationToken ||
                sessionData.participantToken ||
                '',
            qrUrlPath: rawParticipant.qrUrlPath || sessionData.qrUrlPath || '',
            allowedZones: Array.isArray(parsedZones) ? parsedZones : ['GEN', 'FAN_ZONE', 'GASTRO'],
            completedActivationsCount: Number(
                rawParticipant.completedActivationsCount ?? sessionData.completedActivationsCount ?? 0
            ),
            totalPurchasesCount: Number(
                rawParticipant.totalPurchasesCount ?? sessionData.totalPurchasesCount ?? 0
            ),
            totalSpentAmount: Number(
                rawParticipant.totalSpentAmount ?? sessionData.totalSpentAmount ?? 0
            ),
            hasEarnedReward: Boolean(
                rawParticipant.hasEarnedReward ?? sessionData.hasEarnedReward ?? false
            ),
            rewardCouponCode:
                rawParticipant.rewardCouponCode || sessionData.rewardCouponCode || null,
            hasRedeemedReward: Boolean(
                rawParticipant.hasRedeemedReward ?? sessionData.hasRedeemedReward ?? false
            ),
            receivedDigitalBadge: Boolean(
                rawParticipant.receivedDigitalBadge ?? sessionData.receivedDigitalBadge ?? false
            ),
            uniqueStampsCount: Number(
                sessionData.uniqueStampsCount ??
                    rawParticipant.uniqueStampsCount ??
                    stampsList.length
            ),
            hasSponsorActivationStamp: Boolean(
                sessionData.hasSponsorActivationStamp ??
                    rawParticipant.hasSponsorActivationStamp ??
                    false
            ),
            missingMandatorySponsor: Boolean(
                sessionData.missingMandatorySponsor ??
                    rawParticipant.missingMandatorySponsor ??
                    false
            ),
            rewardStatus:
                sessionData.rewardStatus ||
                rawParticipant.rewardStatus ||
                'IN_PROGRESS',
            remainingPhysicalStock: Number(
                sessionData.remainingPhysicalStock ??
                    rawParticipant.remainingPhysicalStock ??
                    300
            ),
            availableStations: stationsList,
            stamps: stampsList,
            journeyLogs: rawParticipant.journeyLogs || sessionData.journeyLogs || [],
            createdAtUtc: rawParticipant.createdAtUtc || sessionData.createdAtUtc || new Date().toISOString()
        };

        const normalizedSession = {
            participantToken:
                sessionData.participantToken ||
                participant.verificationToken ||
                participant.ticketFolio,
            participant,
            config: sessionData.config || null,
            uniqueStampsCount: participant.uniqueStampsCount,
            hasSponsorActivationStamp: participant.hasSponsorActivationStamp,
            missingMandatorySponsor: participant.missingMandatorySponsor,
            rewardStatus: participant.rewardStatus,
            remainingPhysicalStock: participant.remainingPhysicalStock,
            availableStations: stationsList,
            stamps: stampsList,
            isAutoRecovered: Boolean(sessionData.isAutoRecovered),
            savedAtUtc: new Date().toISOString()
        };

        localStorage.setItem(XF_PARTICIPANT_STORAGE_KEY, JSON.stringify(normalizedSession));
        return normalizedSession;
    } catch (error) {
        console.error('Error al guardar la sesión de Xolo Fest en localStorage:', error);
        return null;
    }
};

/**
 * Obtiene la sesión activa del participante de Xolo Fest almacenada en localStorage.
 * @returns {object|null} Objeto con { participantToken, participant, savedAtUtc } o null
 */
export const GetStoredXfParticipantSession = () => {
    try {
        const raw = localStorage.getItem(XF_PARTICIPANT_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.participant || !parsed.participant.ticketFolio) {
            return null;
        }
        return parsed;
    } catch (error) {
        console.error('Error al leer la sesión de Xolo Fest de localStorage:', error);
        return null;
    }
};

/**
 * Elimina la sesión local del participante de Xolo Fest (útil para probar otro boleto en Modo Demo).
 */
export const ClearXfParticipantSession = () => {
    try {
        localStorage.removeItem(XF_PARTICIPANT_STORAGE_KEY);
    } catch (error) {
        console.error('Error al limpiar la sesión de Xolo Fest:', error);
    }
};

/**
 * Valida los campos del formulario de pre-registro de 1 solo paso sin OTP.
 * @param {object} formData - { ticketFolio, fullName, phone, email }
 * @returns {{ isValid: boolean, error: string|null, errors: Record<string, string>, sanitizedData: object }}
 */
export const ValidateXfRegistrationForm = ({ ticketFolio, fullName, phone, email } = {}) => {
    const errors = {};

    const cleanFolio = String(ticketFolio || '').trim().toUpperCase();
    const cleanName = String(fullName || '').trim();
    const cleanPhone = String(phone || '').replace(/[\s\-().+]/g, '').replace(/\D/g, '');
    const cleanEmail = String(email || '').trim().toLowerCase();

    if (!cleanFolio) {
        errors.ticketFolio = 'El folio de tu boleto es obligatorio (ej. XOLO-0001).';
    } else if (cleanFolio.length < 3) {
        errors.ticketFolio = 'Ingresa un folio de boleto válido.';
    }

    if (!cleanName || cleanName.length < 3) {
        errors.fullName = 'Ingresa tu nombre completo (mínimo 3 caracteres).';
    }

    if (cleanPhone.length !== 10) {
        errors.phone = 'Ingresa tu número de Teléfono / WhatsApp a 10 dígitos numéricos.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
        errors.email = 'Ingresa un correo electrónico con formato válido.';
    }

    const errorKeys = Object.keys(errors);
    const isValid = errorKeys.length === 0;

    return {
        isValid,
        error: isValid ? null : errors[errorKeys[0]],
        errors,
        sanitizedData: {
            ticketFolio: cleanFolio,
            fullName: cleanName,
            phone: cleanPhone,
            email: cleanEmail
        }
    };
};

/**
 * Construye e inicia una conexión SignalR en tiempo real hacia el Hub de Xolo Fest (/hub/xf).
 * Suscribe al canal privado del participante (JoinParticipantChannel) y escucha eventos en vivo.
 * @param {string} ticketFolio - Folio del boleto (ej. XOLO-0001)
 * @param {object} handlers - { onStampAdded, onRewardUnlocked, onConfigUpdated, onParticipantReset, onConnectionStateChanged }
 * @returns {{ connection: signalR.HubConnection, stop: () => Promise<void> }}
 */
export const CreateXfPassportHubConnection = (ticketFolio, handlers = {}) => {
    const cleanFolio = String(ticketFolio || '').trim().toUpperCase();

    let hubBaseUrl = '';
    if (process.env.REACT_APP_API_URL) {
        const rawApi = process.env.REACT_APP_API_URL;
        const cleanApi = rawApi.endsWith('/') ? rawApi.slice(0, -1) : rawApi;
        hubBaseUrl = cleanApi.endsWith('/api')
            ? cleanApi.replace(/\/api$/, '/hub/xf')
            : `${cleanApi}/hub/xf`;
    } else if (typeof window !== 'undefined' && window.location && window.location.origin) {
        hubBaseUrl = `${window.location.origin}/hub/xf`;
    } else {
        hubBaseUrl = '/hub/xf';
    }

    const fullHubUrl = cleanFolio
        ? `${hubBaseUrl}?folio=${encodeURIComponent(cleanFolio)}`
        : hubBaseUrl;

    const connection = new signalR.HubConnectionBuilder()
        .withUrl(fullHubUrl)
        .withAutomaticReconnect([0, 1500, 3000, 5000, 10000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    connection.on('XfStampAdded', (payload) => {
        if (typeof handlers.onStampAdded === 'function') {
            handlers.onStampAdded(payload);
        }
    });

    connection.on('XfRewardUnlocked', (payload) => {
        if (typeof handlers.onRewardUnlocked === 'function') {
            handlers.onRewardUnlocked(payload);
        }
    });

    connection.on('XfConfigUpdated', (payload) => {
        if (typeof handlers.onConfigUpdated === 'function') {
            handlers.onConfigUpdated(payload);
        }
    });

    connection.on('XfParticipantReset', (payload) => {
        if (typeof handlers.onParticipantReset === 'function') {
            handlers.onParticipantReset(payload);
        }
    });

    connection.onreconnected(() => {
        if (typeof handlers.onConnectionStateChanged === 'function') {
            handlers.onConnectionStateChanged(true);
        }
        if (cleanFolio) {
            connection.invoke('JoinParticipantChannel', cleanFolio).catch(() => {});
        }
    });

    connection.onclose(() => {
        if (typeof handlers.onConnectionStateChanged === 'function') {
            handlers.onConnectionStateChanged(false);
        }
    });

    connection
        .start()
        .then(() => {
            if (typeof handlers.onConnectionStateChanged === 'function') {
                handlers.onConnectionStateChanged(true);
            }
            if (cleanFolio) {
                connection.invoke('JoinParticipantChannel', cleanFolio).catch(() => {});
            }
        })
        .catch(() => {
            if (typeof handlers.onConnectionStateChanged === 'function') {
                handlers.onConnectionStateChanged(false);
            }
        });

    return {
        connection,
        stop: async () => {
            try {
                await connection.stop();
            } catch {
                // Silencioso al desmontar
            }
        }
    };
};

/**
 * Dibuja un rectángulo con esquinas redondeadas compatible con todos los navegadores móviles.
 */
const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x, y, width, height, radius);
    } else {
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    }
};

/**
 * Dibuja una estrella vectorial de N puntas (para el emblema de Insignia Leyenda Xolos).
 */
const drawStarShape = (ctx, cx, cy, spikes, outerRadius, innerRadius, fillStyle, strokeStyle) => {
    let rot = (Math.PI / 2) * 3;
    let x = cx;
    let y = cy;
    const step = Math.PI / spikes;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    for (let i = 0; i < spikes; i++) {
        x = cx + Math.cos(rot) * outerRadius;
        y = cy + Math.sin(rot) * outerRadius;
        ctx.lineTo(x, y);
        rot += step;

        x = cx + Math.cos(rot) * innerRadius;
        y = cy + Math.sin(rot) * innerRadius;
        ctx.lineTo(x, y);
        rot += step;
    }
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    if (fillStyle) {
        ctx.fillStyle = fillStyle;
        ctx.fill();
    }
    if (strokeStyle) {
        ctx.lineWidth = 3;
        ctx.strokeStyle = strokeStyle;
        ctx.stroke();
    }
    ctx.restore();
};

/**
 * Renderiza un código QR de alta resolución desde QRCodeSVG y lo dibuja sobre el contexto Canvas 2D.
 */
const drawQrCodeOnCanvas = async (ctx, qrValue, x, y, size, fgColor = '#09090b') => {
    const svgString = renderToStaticMarkup(
        React.createElement(QRCodeSVG, {
            value: qrValue || 'XOLO-FEST-2026',
            size: size - 24,
            bgColor: '#ffffff',
            fgColor,
            level: 'Q',
            includeMargin: true
        })
    );

    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const qrImg = new Image();

    await new Promise((resolve) => {
        qrImg.onload = () => {
            ctx.drawImage(qrImg, x, y, size, size);
            URL.revokeObjectURL(svgUrl);
            resolve();
        };
        qrImg.onerror = () => {
            URL.revokeObjectURL(svgUrl);
            resolve();
        };
        qrImg.src = svgUrl;
    });
};

/**
 * Generador gráfico en Canvas 2D (720 x 980 px) para descargar como archivo PNG:
 * - 'GOLDEN_COUPON': Cupón Dorado Oficial (#CANJE-XXXXXX)
 * - 'DIGITAL_BADGE': Insignia Conmemorativa Digital ("Fan Leyenda Xolos 2026")
 * - 'RAFFLE_PASS': Pase Oficial de la Tómbola Perrona (#TOMB-...)
 *
 * @param {object} participant - Datos del participante
 * @param {object} config - Configuración activa del evento
 * @param {'GOLDEN_COUPON'|'DIGITAL_BADGE'|'RAFFLE_PASS'} cardType - Tipo de tarjeta a exportar
 * @returns {Promise<boolean>}
 */
export const DownloadXfRewardCardPng = async (
    participant,
    config = null,
    cardType = 'GOLDEN_COUPON'
) => {
    if (!participant) return false;

    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const width = 720;
        const height = 980;
        canvas.width = width;
        canvas.height = height;

        const folio = participant.ticketFolio || 'XOLO-0000';
        const fullName = (participant.fullName || 'AFICIONADO XOLO').toUpperCase();
        const category = (participant.ticketCategory || 'GENERAL').toUpperCase();
        const eventTitle = (config?.eventName || 'XOLO FEST 2026').toUpperCase();

        // 1. Fondo Exterior
        const bgGrad = ctx.createLinearGradient(0, 0, width, height);
        if (cardType === 'GOLDEN_COUPON') {
            bgGrad.addColorStop(0, '#09090b');
            bgGrad.addColorStop(0.45, '#271705');
            bgGrad.addColorStop(1, '#09090b');
        } else if (cardType === 'DIGITAL_BADGE') {
            bgGrad.addColorStop(0, '#09090b');
            bgGrad.addColorStop(0.5, '#450a0a');
            bgGrad.addColorStop(1, '#09090b');
        } else {
            bgGrad.addColorStop(0, '#09090b');
            bgGrad.addColorStop(0.5, '#1f0808');
            bgGrad.addColorStop(1, '#09090b');
        }
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);

        // 2. Marco Doble Dorado / Carmesí
        const margin = 28;
        drawRoundedRect(ctx, margin, margin, width - margin * 2, height - margin * 2, 26);
        ctx.fillStyle = 'rgba(15, 15, 20, 0.94)';
        ctx.fill();
        ctx.lineWidth = 6;
        ctx.strokeStyle = '#d4af37';
        ctx.stroke();

        drawRoundedRect(
            ctx,
            margin + 12,
            margin + 12,
            width - (margin + 12) * 2,
            height - (margin + 12) * 2,
            18
        );
        ctx.lineWidth = 2;
        ctx.strokeStyle = cardType === 'DIGITAL_BADGE' ? '#dc2626' : 'rgba(212, 175, 55, 0.55)';
        ctx.stroke();

        ctx.textAlign = 'center';

        // 3. Membrete Superior Club Tijuana
        ctx.fillStyle = '#f59e0b';
        ctx.font = '800 15px Montserrat, Arial, sans-serif';
        ctx.fillText('★ CLUB TIJUANA XOLOITZCUINTLES DE CALIENTE ★', width / 2, 86);

        ctx.fillStyle = '#a1a1aa';
        ctx.font = '700 13px Montserrat, Arial, sans-serif';
        ctx.fillText(eventTitle, width / 2, 110);

        // Divisor superior
        const divGrad = ctx.createLinearGradient(90, 126, width - 90, 126);
        divGrad.addColorStop(0, 'transparent');
        divGrad.addColorStop(0.5, '#d4af37');
        divGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = divGrad;
        ctx.fillRect(90, 125, width - 180, 2);

        if (cardType === 'GOLDEN_COUPON') {
            // Encabezado Cupón Dorado
            ctx.fillStyle = '#fde68a';
            ctx.font = '900 32px Montserrat, Arial, sans-serif';
            ctx.fillText('¡FELICIDADES, AFICIONADO XOLO!', width / 2, 172);

            ctx.fillStyle = '#ffffff';
            ctx.font = '700 18px Montserrat, Arial, sans-serif';
            ctx.fillText('CUPÓN DORADO OFICIAL DE RECOMPENSA', width / 2, 204);

            const prizeTitle =
                config?.rewardPrizeTitle || 'Vaso Conmemorativo Oficial Xolo Fest';
            ctx.fillStyle = '#f59e0b';
            ctx.font = '800 22px Montserrat, Arial, sans-serif';
            ctx.fillText(prizeTitle, width / 2, 240);

            // Banner Dorado Brillante con Folio #CANJE-XXXXXX
            const bannerX = 75;
            const bannerY = 266;
            const bannerW = width - 150;
            const bannerH = 120;
            const goldGrad = ctx.createLinearGradient(
                bannerX,
                bannerY,
                bannerX + bannerW,
                bannerY + bannerH
            );
            goldGrad.addColorStop(0, '#b45309');
            goldGrad.addColorStop(0.5, '#f59e0b');
            goldGrad.addColorStop(1, '#b45309');

            drawRoundedRect(ctx, bannerX, bannerY, bannerW, bannerH, 18);
            ctx.fillStyle = goldGrad;
            ctx.fill();

            ctx.fillStyle = '#09090b';
            ctx.font = '900 15px Montserrat, Arial, sans-serif';
            ctx.fillText('FOLIO ÚNICO DE CANJE FÍSICO EN STAND', width / 2, bannerY + 34);

            const couponCode = participant.rewardCouponCode || `CANJE-${folio}`;
            ctx.fillStyle = '#ffffff';
            ctx.font = '900 38px "Courier New", monospace';
            ctx.fillText(`#${couponCode.replace(/^#/, '')}`, width / 2, bannerY + 86);

            // QR de Alta Resolución
            const qrBoxSize = 250;
            const qrBoxX = (width - qrBoxSize) / 2;
            const qrBoxY = 412;
            drawRoundedRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 20);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 4;
            ctx.strokeStyle = '#d4af37';
            ctx.stroke();

            await drawQrCodeOnCanvas(
                ctx,
                couponCode || participant.verificationToken || folio,
                qrBoxX + 12,
                qrBoxY + 12,
                qrBoxSize - 24,
                '#09090b'
            );

            // Datos del Aficionado
            ctx.fillStyle = '#ffffff';
            ctx.font = '800 24px Montserrat, Arial, sans-serif';
            ctx.fillText(fullName, width / 2, 710);

            ctx.fillStyle = '#fde68a';
            ctx.font = '700 16px "Courier New", monospace';
            ctx.fillText(`BOLETO: ${folio}  •  CATEGORÍA: ${category}`, width / 2, 742);

            ctx.fillStyle = '#a1a1aa';
            ctx.font = '600 14px Montserrat, Arial, sans-serif';
            ctx.fillText(
                'Presenta este Cupón Dorado en el Módulo de Recompensas Xolo Fest',
                width / 2,
                780
            );
        } else if (cardType === 'DIGITAL_BADGE') {
            // Encabezado Insignia Conmemorativa Digital
            ctx.fillStyle = '#fde68a';
            ctx.font = '900 30px Montserrat, Arial, sans-serif';
            ctx.fillText('★ FAN LEYENDA XOLOS 2026 ★', width / 2, 170);

            const badgeTitle = config?.digitalBadgeTitle || 'Insignia Leyenda Xolos 2026';
            ctx.fillStyle = '#ffffff';
            ctx.font = '800 21px Montserrat, Arial, sans-serif';
            ctx.fillText(badgeTitle.toUpperCase(), width / 2, 204);

            // Emblema Vectorial Central (Escudo Circular + Estrella Dorada)
            const emblemY = 305;
            ctx.save();
            ctx.beginPath();
            ctx.arc(width / 2, emblemY, 68, 0, Math.PI * 2);
            const shieldGrad = ctx.createRadialGradient(
                width / 2,
                emblemY,
                10,
                width / 2,
                emblemY,
                68
            );
            shieldGrad.addColorStop(0, '#dc2626');
            shieldGrad.addColorStop(0.7, '#7f1d1d');
            shieldGrad.addColorStop(1, '#09090b');
            ctx.fillStyle = shieldGrad;
            ctx.fill();
            ctx.lineWidth = 4;
            ctx.strokeStyle = '#f59e0b';
            ctx.stroke();
            ctx.restore();

            drawStarShape(ctx, width / 2, emblemY, 5, 42, 19, '#fde68a', '#b45309');

            // Banner Conmemorativo
            const bannerX = 75;
            const bannerY = 395;
            const bannerW = width - 150;
            const bannerH = 86;
            drawRoundedRect(ctx, bannerX, bannerY, bannerW, bannerH, 16);
            ctx.fillStyle = 'rgba(220, 38, 38, 0.25)';
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#f59e0b';
            ctx.stroke();

            ctx.fillStyle = '#fde68a';
            ctx.font = '800 15px Montserrat, Arial, sans-serif';
            ctx.fillText('CERTIFICADO OFICIAL DE RECORRIDO COMPLETADO', width / 2, bannerY + 34);

            const stampsDone = participant.uniqueStampsCount || (participant.stamps?.length ?? 4);
            ctx.fillStyle = '#ffffff';
            ctx.font = '900 22px Montserrat, Arial, sans-serif';
            ctx.fillText(
                `${stampsDone} ESTACIONES OFICIALES COMPLETADAS`,
                width / 2,
                bannerY + 66
            );

            // QR Conmemorativo
            const qrBoxSize = 200;
            const qrBoxX = (width - qrBoxSize) / 2;
            const qrBoxY = 505;
            drawRoundedRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 18);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 3;
            ctx.strokeStyle = '#f59e0b';
            ctx.stroke();

            await drawQrCodeOnCanvas(
                ctx,
                participant.verificationToken || folio,
                qrBoxX + 10,
                qrBoxY + 10,
                qrBoxSize - 20,
                '#09090b'
            );

            // Datos del Aficionado
            ctx.fillStyle = '#ffffff';
            ctx.font = '800 24px Montserrat, Arial, sans-serif';
            ctx.fillText(fullName, width / 2, 748);

            ctx.fillStyle = '#fde68a';
            ctx.font = '700 16px "Courier New", monospace';
            ctx.fillText(`FOLIO OFICIAL: #${folio}  •  CATEGORÍA: ${category}`, width / 2, 780);
        } else {
            // 'RAFFLE_PASS' (Pase Tómbola Perrona Pre-Evento)
            ctx.fillStyle = '#fde68a';
            ctx.font = '900 32px Montserrat, Arial, sans-serif';
            ctx.fillText('🎰 PASE OFICIAL TÓMBOLA PERRONA', width / 2, 175);

            ctx.fillStyle = '#ffffff';
            ctx.font = '700 18px Montserrat, Arial, sans-serif';
            ctx.fillText('INSCRIPCIÓN CONFIRMADA Y PASE DE ACCESO', width / 2, 208);

            const bannerX = 75;
            const bannerY = 240;
            const bannerW = width - 150;
            const bannerH = 120;
            drawRoundedRect(ctx, bannerX, bannerY, bannerW, bannerH, 18);
            ctx.fillStyle = 'rgba(220, 38, 38, 0.25)';
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#f59e0b';
            ctx.stroke();

            ctx.fillStyle = '#fde68a';
            ctx.font = '800 15px Montserrat, Arial, sans-serif';
            ctx.fillText('FOLIO PARTICIPANTE EN EL GRAN SORTEO', width / 2, bannerY + 36);

            const raffleCode = participant.raffleTicketCode || folio;
            ctx.fillStyle = '#ffffff';
            ctx.font = '900 38px "Courier New", monospace';
            ctx.fillText(`#${raffleCode.replace(/^#/, '')}`, width / 2, bannerY + 86);

            const qrBoxSize = 260;
            const qrBoxX = (width - qrBoxSize) / 2;
            const qrBoxY = 390;
            drawRoundedRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 20);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 4;
            ctx.strokeStyle = '#f59e0b';
            ctx.stroke();

            await drawQrCodeOnCanvas(
                ctx,
                participant.verificationToken || folio,
                qrBoxX + 12,
                qrBoxY + 12,
                qrBoxSize - 24,
                '#09090b'
            );

            ctx.fillStyle = '#ffffff';
            ctx.font = '800 24px Montserrat, Arial, sans-serif';
            ctx.fillText(fullName, width / 2, 700);

            ctx.fillStyle = '#fde68a';
            ctx.font = '700 16px "Courier New", monospace';
            ctx.fillText(`BOLETO: ${folio}  •  CATEGORÍA: ${category}`, width / 2, 735);

            ctx.fillStyle = '#a1a1aa';
            ctx.font = '600 14px Montserrat, Arial, sans-serif';
            ctx.fillText(
                'Presenta este código QR en las puertas del estadio para tu Check-In',
                width / 2,
                775
            );
        }

        // Pie institucional
        ctx.fillStyle = 'rgba(212, 175, 55, 0.35)';
        ctx.fillRect(90, 865, width - 180, 1.5);

        ctx.fillStyle = '#a1a1aa';
        ctx.font = '700 12px Montserrat, Arial, sans-serif';
        ctx.fillText(
            'CLUB TIJUANA XOLOITZCUINTLES DE CALIENTE • INTERAKTIA V2',
            width / 2,
            898
        );

        ctx.fillStyle = '#71717a';
        ctx.font = '600 11px "Courier New", monospace';
        ctx.fillText(
            `AUTENTICACIÓN CRIPTOGRÁFICA HMAC-SHA256 • ${new Date().toLocaleDateString('es-MX')}`,
            width / 2,
            920
        );

        // Disparar descarga PNG
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        const cleanFolioSlug = folio.toLowerCase().replace(/[^a-z0-9]/g, '-');
        const prefixSlug =
            cardType === 'GOLDEN_COUPON'
                ? 'xolofest-cupon'
                : cardType === 'DIGITAL_BADGE'
                ? 'xolofest-insignia'
                : 'xolofest-tombola';
        link.download = `${prefixSlug}-${cleanFolioSlug}.png`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return true;
    } catch (error) {
        console.error('Error al generar tarjeta PNG en Canvas 2D:', error);
        return false;
    }
};

// ============================================================================
// GESTIÓN DE SESIÓN DE OPERADOR Y SIGNALR DEL SCANNER WEB PWA (HU-04)
// ============================================================================

/**
 * Normaliza y persiste en localStorage la sesión activa del Operador del Scanner PWA.
 * @param {object} session - { token, username, role, expiresInMinutes, stationCode }
 * @returns {object|null}
 */
export const SaveXfOperatorSession = (session) => {
    if (!session || !session.token) return null;

    try {
        const expiresInMinutes = Number(session.expiresInMinutes || 60);
        const nowMs = Date.now();
        const expiresAtUtc =
            session.expiresAtUtc || new Date(nowMs + expiresInMinutes * 60 * 1000).toISOString();

        const normalized = {
            token: String(session.token).trim(),
            username: String(session.username || 'staff_puerta1').trim(),
            role: String(session.role || 'Operador').trim(),
            expiresInMinutes,
            expiresAtUtc,
            selectedStationCode: session.selectedStationCode || 'P1_ACCESO',
            savedAtUtc: new Date(nowMs).toISOString()
        };

        localStorage.setItem(XF_OPERATOR_STORAGE_KEY, JSON.stringify(normalized));
        return normalized;
    } catch (error) {
        console.error('Error al guardar sesión de operador del Scanner en localStorage:', error);
        return null;
    }
};

/**
 * Obtiene la sesión activa del Operador del Scanner desde localStorage, verificando que no haya expirado.
 * @returns {object|null}
 */
export const GetStoredXfOperatorSession = () => {
    try {
        const raw = localStorage.getItem(XF_OPERATOR_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.token) {
            return null;
        }

        if (parsed.expiresAtUtc) {
            const expTime = new Date(parsed.expiresAtUtc).getTime();
            if (!isNaN(expTime) && Date.now() > expTime) {
                localStorage.removeItem(XF_OPERATOR_STORAGE_KEY);
                return null;
            }
        }

        return parsed;
    } catch (error) {
        console.error('Error al leer sesión de operador del Scanner de localStorage:', error);
        return null;
    }
};

/**
 * Elimina la sesión local del Operador del Scanner (logout o bloqueo por inactividad).
 */
export const ClearXfOperatorSession = () => {
    try {
        localStorage.removeItem(XF_OPERATOR_STORAGE_KEY);
    } catch (error) {
        console.error('Error al limpiar sesión de operador del Scanner:', error);
    }
};

/**
 * Construye una conexión SignalR en tiempo real hacia `/hub/xf?role=board` para el Scanner Web PWA,
 * recibiendo actualizaciones en vivo de aforo de zonas (`XfZoneOccupancyUpdated`) y configuración (`XfConfigUpdated`).
 * @param {object} handlers - { onZoneOccupancyUpdated, onConfigUpdated, onStampAdded, onParticipantReset, onConnectionStateChanged }
 * @returns {{ connection: signalR.HubConnection, stop: () => Promise<void> }}
 */
export const CreateXfScannerHubConnection = (handlers = {}) => {
    let hubBaseUrl = '';
    if (process.env.REACT_APP_API_URL) {
        const rawApi = process.env.REACT_APP_API_URL;
        const cleanApi = rawApi.endsWith('/') ? rawApi.slice(0, -1) : rawApi;
        hubBaseUrl = cleanApi.endsWith('/api')
            ? cleanApi.replace(/\/api$/, '/hub/xf')
            : `${cleanApi}/hub/xf`;
    } else if (typeof window !== 'undefined' && window.location && window.location.origin) {
        hubBaseUrl = `${window.location.origin}/hub/xf`;
    } else {
        hubBaseUrl = '/hub/xf';
    }

    const fullHubUrl = `${hubBaseUrl}?role=board`;

    const connection = new signalR.HubConnectionBuilder()
        .withUrl(fullHubUrl)
        .withAutomaticReconnect([0, 1500, 3000, 5000, 10000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    connection.on('XfZoneOccupancyUpdated', (payload) => {
        if (typeof handlers.onZoneOccupancyUpdated === 'function') {
            handlers.onZoneOccupancyUpdated(payload);
        }
    });

    connection.on('XfConfigUpdated', (payload) => {
        if (typeof handlers.onConfigUpdated === 'function') {
            handlers.onConfigUpdated(payload);
        }
    });

    connection.on('XfStampAdded', (payload) => {
        if (typeof handlers.onStampAdded === 'function') {
            handlers.onStampAdded(payload);
        }
    });

    connection.on('XfParticipantReset', (payload) => {
        if (typeof handlers.onParticipantReset === 'function') {
            handlers.onParticipantReset(payload);
        }
    });

    connection.on('XfRewardUnlocked', (payload) => {
        if (typeof handlers.onRewardUnlocked === 'function') {
            handlers.onRewardUnlocked(payload);
        }
    });

    connection.onreconnected(() => {
        if (typeof handlers.onConnectionStateChanged === 'function') {
            handlers.onConnectionStateChanged(true);
        }
        connection.invoke('JoinBoardChannel').catch(() => {});
    });

    connection.onclose(() => {
        if (typeof handlers.onConnectionStateChanged === 'function') {
            handlers.onConnectionStateChanged(false);
        }
    });

    connection
        .start()
        .then(() => {
            if (typeof handlers.onConnectionStateChanged === 'function') {
                handlers.onConnectionStateChanged(true);
            }
            connection.invoke('JoinBoardChannel').catch(() => {});
        })
        .catch(() => {
            if (typeof handlers.onConnectionStateChanged === 'function') {
                handlers.onConnectionStateChanged(false);
            }
        });

    return {
        connection,
        stop: async () => {
            try {
                await connection.stop();
            } catch {
                // Silencioso al desmontar
            }
        }
    };
};

// ============================================================================
// UTILIDADES DEL CENTRO DE MANDO LIVE BOARD E INSPECTOR 360° (HU-06)
// ============================================================================

/**
 * Calcula el estado del semáforo de Protección Civil según el porcentaje de ocupación de una zona.
 * - < 70%: GREEN ("AFORO NORMAL")
 * - 70% - 89.9%: YELLOW ("PRECAUCIÓN")
 * - >= 90%: RED ("ALERTA SOBRECUPERO")
 * @param {number} percentage - Porcentaje de ocupación (0 a 100+)
 * @returns {{ status: 'GREEN'|'YELLOW'|'RED', label: string, colorClass: string, bgClass: string, borderClass: string, hexColor: string, bgRgba: string, borderRgba: string }}
 */
export const getOccupancySemaphore = (percentage = 0) => {
    const pct = Number(percentage || 0);

    if (pct >= 90) {
        return {
            status: 'RED',
            label: 'ALERTA SOBRECUPERO',
            colorClass: 'text-danger',
            bgClass: 'bg-danger',
            borderClass: 'border-danger',
            hexColor: '#dc2626',
            bgRgba: 'rgba(220, 38, 38, 0.18)',
            borderRgba: 'rgba(220, 38, 38, 0.65)'
        };
    }

    if (pct >= 70) {
        return {
            status: 'YELLOW',
            label: 'PRECAUCIÓN',
            colorClass: 'text-warning',
            bgClass: 'bg-warning',
            borderClass: 'border-warning',
            hexColor: '#f59e0b',
            bgRgba: 'rgba(245, 158, 11, 0.18)',
            borderRgba: 'rgba(245, 158, 11, 0.65)'
        };
    }

    return {
        status: 'GREEN',
        label: 'AFORO NORMAL',
        colorClass: 'text-success',
        bgClass: 'bg-success',
        borderClass: 'border-success',
        hexColor: '#10b981',
        bgRgba: 'rgba(16, 185, 129, 0.18)',
        borderRgba: 'rgba(16, 185, 129, 0.55)'
    };
};

export const GetOccupancySemaphore = getOccupancySemaphore;

export {
    GetXfPublicConfig,
    ValidateXfTicketToken,
    RegisterXfParticipant,
    RecoverXfPass,
    GetXfDemoSampleTickets,
    GetXfPassportByFolio,
    UpdateXfAdminConfig,
    SimulateXfStamp,
    SimulateXfStock,
    ResetXfParticipantDemo,
    AuthenticateXfDemoOperator,
    LoginXfRealOperator,
    GetXfScannerStations,
    GetXfScannerZones,
    CheckXfAccessScan,
    GetXfRecentAccessLogs,
    GetXfStationProducts,
    LookupXfParticipantForScanner,
    RecordXfPosPurchase,
    RecordXfSponsorActivation,
    RedeemXfRewardCoupon,
    GetXfLiveBoardMetrics,
    SearchXfParticipants,
    GetXfParticipant360,
    SimulateXfTraffic,
    ResetXfDemoTraffic,
    ResetXfAllData,
    ExportXfExcelReport,
    GetOfflineScanQueue,
    EnqueueOfflineScan,
    ClearOfflineScanQueue,
    ProcessOfflineScanQueue
};

